import {TypeUtils} from "./type-utils";
import {ActivatedRouteSnapshot, UrlSegment} from "@angular/router";
import {HttpParams} from "@angular/common/http";
import * as CronParser from 'cron-parser';
import {AbstractControl, ValidationErrors, ValidatorFn} from "@angular/forms";
import {Config} from "../../config/config";
import {UserSubscription} from "../../user/user-subscription";

export class Utils {

  constructor() {
  }

  public static generateUid(): string {
    function s4(): string {
      return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
  }

  public static findCommonElement(array1, array2) {
    for (let i = 0; i < array1.length; i++) {
      for (let j = 0; j < array2.length; j++) {
        if (array1[i] === array2[j]) {
          return true;
        }
      }
    }
    return false;
  }

  public static generateRandomPassword(length): string {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz',
      uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      numbers = '0123456789',
      symbols = '!@#$%^&*()+_-=}{[]|:;?.`~';
    let string = "";
    for (let i = 0; i < Math.floor(length / 4); i++) {
      string += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
      string += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
      string += numbers.charAt(Math.floor(Math.random() * numbers.length));
      string += symbols.charAt(Math.floor(Math.random() * symbols.length));
    }

    return string;
  }

  public static capitalize(word: string): string {
    return word.charAt(0).toUpperCase() + word.substr(1);
  }

  // Function to check if a date string is older than one day
  public static isOlderThanOneDay(dateString): boolean {
    const oneDayInMillis = 24 * 60 * 60 * 1000; // One day in milliseconds
    const difference = new Date().getTime() - parseInt(dateString, 10);
    return difference >= oneDayInMillis;
  }

  public static replaceUrl(value: string | String): string | String {
    return value.replace('https://', '').replace('http://', '')
      .replace('www.', '').replace(/\/$/, "");
  }

  public static capitalizeAll(word: string): string {
    // TODO Seroj said to change 'client management' to 'users management' (we don't want to say core to change)
    if (word === 'payment info') {
      return '10Web Payments';
    } else if (word === 'client management') {
      return 'Users Management';
    } else {
      return word.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
    }
  }

  public static checkArrayChanges(originalArray, newArray) {
    if (originalArray.length !== newArray.length) {
      return true; // Array lengths are different, so values have changed
    }

    for (let i = 0; i < originalArray.length; i++) {
      if (originalArray[i] !== newArray[i]) {
        return true; // Value at index i has changed
      }
    }

    return false; // Array values have not changed
  }

  public static roundNumber(num: number): number {
    return Math.round(num * 100) / 100;
  }

  public static roundNumberHalfUp(num: number, precision: number): number {
    let factor = Math.pow(10, precision);
    return Math.round(num * factor) / factor;
  }

  public static detectCardType(cardNumber: number | string): string | undefined {
    cardNumber = cardNumber.toString().replace(/[-\s]/g, '');
    let re: any = {
      maestro: /^(5018|5020|5038|5612|5893|6304|6759|6761|6762|6763|0604|6390)/,
      visa: /^4/,
      mastercard: /^5[1-5][0-9]/,
      amex: /^3[47][0-9]/,
      discover: /^6(?:011|5[0-9]{2})/
    };

    if (re.maestro.test(cardNumber)) {
      return 'maestro';
    } else if (re.visa.test(cardNumber)) {
      return 'visa';
    } else if (re.mastercard.test(cardNumber)) {
      return 'mastercard';
    } else if (re.amex.test(cardNumber)) {
      return 'amex';
    } else if (re.discover.test(cardNumber)) {
      return 'discover';
    }

    return undefined;
  }

  public static sizeToString(bytes: number, unit: string = "B"): string {
    let size: number;
    let sizeString: string;
    switch (unit) {
      case 'KB':
        bytes = bytes * 1024;
        break;
      case 'MB':
        bytes = bytes * 1024 * 1024;
        break;
      case 'GB':
        bytes = bytes * 1024 * 1024 * 1024;
        break;
    }

    if (isNaN(bytes)) {
      sizeString = '';
    } else if (bytes < 1024) {
      size = bytes;
      unit = 'B';
    } else if (bytes < 1024 * 1024) {
      size = bytes / 1024;
      unit = 'KB';
    } else if (bytes < 1024 * 1024 * 1024) {
      size = bytes / (1024 * 1024);
      unit = 'MB';
    } else {
      size = bytes / (1024 * 1024 * 1024);
      unit = 'GB';
    }

    if (size !== undefined) {
      size = Math.round(size * 100) / 100;
      sizeString = size + '<span>' + unit + '</span>';
    }

    return sizeString;
  }

  public static progressPercent(controlName): number {
    if (controlName.errors && controlName.value) {
      if (Object.keys(controlName.errors).length === 2) {
        return 100 / 3;
      } else if (Object.keys(controlName.errors).length === 1) {
        return 100 * 2 / 3;
      } else {
        return 0;
      }
    } else if (controlName.errors === null) {
      return 100;
    } else if ((Object.keys(controlName.errors).length === 1 && !controlName.value)) {
      return 0;
    }
    return 0;
  }

  public static patternValidator(regex: RegExp, error?: ValidationErrors, reverse = false): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = reverse ? !regex.test(control.value) : regex.test(control.value);
      return valid ? null : error;
    };
  }

  public static makeOptionsReadable(options): any {
    return TypeUtils.isDefined(options) ? Object.entries(options).map(([key, value]) => {
      return {value: key || 'standard', label: value};
    }) : [];
  }

  public static countToString(counts: number, unit: string = "K", plusMode = false): string {
    let count: number;
    let countString: string;
    switch (unit) {
      case 'K':
        counts = counts * 1000;
        break;
      case 'M':
        counts = counts * 1000 * 1000;
        break;
      case 'B':
        counts = counts * 1000 * 1000 * 1000;
        break;
    }

    if (isNaN(counts)) {
      countString = '';
    } else if (counts < 1000 * 1000) {
      count = counts / 1000;
      unit = '';
    } else if (counts < 1000 * 1000 * 1000) {
      count = counts / (1000 * 1000);
      unit = 'K';
    } else if (counts < 1000 * 1000 * 1000 * 1000) {
      count = counts / (1000 * 1000 * 1000);
      unit = 'M';
    } else {
      count = counts / (1000 * 1000 * 1000 * 1000);
      unit = 'B';
    }

    if (plusMode && count !== undefined) {
      const base = Math.floor(count);
      const isRounded = count === base;
      countString = (isRounded ? base : base + '+') + `<span>${unit}</span>`;
      return countString
    }

    if (count !== undefined) {
      count = Math.round(count * 100) / 100;
      countString = count + '<span>' + unit + '</span>';
    }

    return countString;
  }

  public static isTruthy(value: any, websiteconnected = false): boolean {
    return [true, 'true', 1, '1'].indexOf(value) !== -1;
  }

  public static trimValue(el): string {
    const value = el.value || el;
    return value.replace(/(^\s*)|(\s*$)/gi, ""). // removes leading and trailing spaces
      replace(/[ ]{2,}/gi, " ").       // replaces multiple spaces with one space
      replace(/\n +/, "\n");           // Removes spaces after newlines
  }

  public static parseVersion(version: string): string {
    if (TypeUtils.isString(version)) {
      let versionMatches: string[] = version.match(/[0-9.]+$/);
      version = versionMatches && versionMatches.length ? versionMatches[0] : '';
    } else {
      version = '';
    }

    return version;
  }

  public static isUpdateAvailable(currentVersion: string, newVersion: string): boolean {
    if (!currentVersion || !TypeUtils.isString(currentVersion) || !newVersion || !TypeUtils.isString(newVersion)) {
      return false;
    }

    const currentVersionParts: number[] = currentVersion.split('.').map((partStr: string): number => parseInt(partStr));
    const newVersionParts: number[] = newVersion.split('.').map((partStr: string): number => parseInt(partStr));

    return newVersionParts.reduce((versionDiff: number, newVersionPart: number, index: number): number => {
      if (versionDiff === 0) {
        const currentVersionPart: number = currentVersionParts[index] ? currentVersionParts[index] : 0;
        versionDiff = newVersionPart - currentVersionPart;
      }

      return versionDiff;
    }, 0) > 0;
  }

  public static sortObjectByKey(obj, firstKey): object {
    const sortedKeys = Object.keys(obj).sort((a, b) => {
      if (a === firstKey) return -1; // "firstKey" comes first
      if (b === firstKey) return 1; // "firstKey" comes second
      return a.localeCompare(b); // Sort the rest alphabetically
    });

    const sortedObject = {};

    for (const key of sortedKeys) {
      sortedObject[key] = obj[key];
    }

    return sortedObject;
  }

  public static secondsToTime(seconds: number): string {
    const parsedDays: number = Math.floor(seconds / (60 * 60 * 24));
    seconds -= parsedDays * 60 * 60 * 24;
    const parsedHours: number = Math.floor(seconds / (60 * 60));
    seconds -= parsedHours * 60 * 60;
    const parsedMinutes: number = Math.floor(seconds / 60);
    seconds -= parsedMinutes * 60;
    const parsedSeconds: number = seconds;

    if (parsedDays) {
      if (parsedDays === 1) {
        return parsedDays + " day";
      }
      return parsedDays + " days";
    }
    if (parsedHours) {
      if (parsedHours === 1) {
        return parsedHours + " hour";
      }
      return parsedHours + " hours";
    } else if (parsedMinutes) {
      if (parsedMinutes === 1) {
        return parsedMinutes + " minute";
      }
      return parsedMinutes + " minutes";
    } else if (parsedSeconds) {
      if (parsedSeconds === 1) {
        return parsedSeconds + " second";
      }
      return parsedSeconds + " seconds";
    }
    return `${parsedSeconds}`;
  }

  public static addZeroes(value: number, fieldsCount: number): string {
    let valueStr = value.toString();

    if (valueStr.length >= fieldsCount) {
      return valueStr;
    }

    return new Array(fieldsCount + 1 - valueStr.length).join('0') + valueStr;
  }

  public static addQueryString(url: string, queryString: string): string {
    return url + (url.indexOf('?') !== -1 ? '&' : '?') + queryString;
  }

  public static unique(items: any[]): any[] {
    return items.reduce((uniqueItems: any[], item: any) => {
      uniqueItems.indexOf(item) === -1 && uniqueItems.push(item);
      return uniqueItems;
    }, []);
  }

  public static arrayDifference(arr1: any[], arr2: any[]): any[] {
    return arr1.filter((item: any): boolean => {
      return !arr2.includes(item);
    });
  }

  public static arraysEqual(arr1, arr2): any {
    if (typeof arr1 !== 'undefined' && typeof arr2 !== 'undefined') {
      if (arr1.length !== arr2.length) {
        return false;
      }
      for (var i = arr1.length; i--;) {
        if (arr1[i] !== arr2[i]) {
          return false;
        }
      }
    }
    return true;
  }

  public static arrayContainsAnotherArray(arr1, arr2) {
    if (typeof arr1 !== 'undefined' && typeof arr2 !== 'undefined') {
      if (arr1.length < arr2.length) {
        return arr1.every(elem => arr2.includes(elem));
      } else {
        return arr2.every(elem => arr1.includes(elem));
      }
    }
    return false;
  }


  public static arrayContains(arr1, arr2): boolean {
    const array = arr1 && arr2 && arr1.filter(elem => arr2.includes(elem));
    return !TypeUtils.isEmpty(array);
  }


  public static compareArrays(arr1, arr2): boolean {
    if (typeof arr1 !== 'undefined' && typeof arr2 !== 'undefined') {
      if (arr1.length > arr2.length) {
        return arr1.filter(function (val) {
          return arr2.indexOf(val) != -1;
        });
      } else {
        return arr2.filter(function (val) {
          return arr1.indexOf(val) != -1;
        });
      }
    }
    return true;
  }

  public static getUrlFromRouteSnapshot(routeSnapshot: ActivatedRouteSnapshot): string {
    let urlParts: string[] = [];

    while (routeSnapshot) {
      urlParts = routeSnapshot.url.map((urlSegment: UrlSegment) => urlSegment.path).concat(urlParts);
      routeSnapshot = routeSnapshot.parent;
    }

    return '/' + urlParts.join('/');
  }

  public static httpParamsFromObject(obj: any): HttpParams {
    let httpParams: HttpParams = new HttpParams();

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        httpParams = httpParams.append(key, obj[key]);
      }

    }

    return httpParams;
  }

  public static stringifyUrlParams(params: any, keyChain: string = ''): string {
    if (!TypeUtils.isObject(params)) {
      return [0, false].indexOf(params) !== -1 ? params.toString() : '';
    }

    const urlParamParts: string[] = Object.keys(params).map((key: string): string => {
      const value: any = params[key];
      const childKeyChain: string = keyChain ? `${keyChain}[${key}]` : key;

      if (TypeUtils.isObject(value)) {
        return Utils.stringifyUrlParams(value, childKeyChain);
      }

      return `${childKeyChain}=${value}`;
    });

    return urlParamParts.join('&');
  }

  public static getValueByKeyChain(obj: any, keyChain: string): any {
    const keys = keyChain.split('.');
    let key = '';
    do {
      key += keys.shift();

      if (!TypeUtils.isUndefined(obj) && TypeUtils.isObject(obj[key]) || !keys.length) {
        obj = obj[key];
        key = '';
      } else if (!keys.length) {
        obj = undefined;
      } else {
        key += '.';
      }
    } while (keys.length);

    return obj;
  }

  public static sort(arr: any[], sortFunction: Function): any[] {
    const elementWrappers: { index: number, element: any }[] = arr.map((element: any, index: number): any => {
      return {
        index,
        element
      };
    });

    elementWrappers.sort((elementWrapper1: { index: number, element: any }, elementWrapper2: {
      index: number,
      element: any
    }): number => {
      let sortResult: number = sortFunction(elementWrapper1.element, elementWrapper2.element);

      if (sortResult === 0) {
        sortResult = elementWrapper1.index - elementWrapper2.index;
      }
      return sortResult;
    });

    return elementWrappers.map((elementWrapper: { index: number, element: any }): any => elementWrapper.element);
  }

  public static getPerformanceGrade(score): string {
    if (score >= 90) {
      return 'a';
    } else if (score >= 80) {
      return 'b';
    } else if (score >= 70) {
      return 'c';
    } else if (score >= 60) {
      return 'd';
    } else if (score >= 50) {
      return 'e';
    } else {
      return 'f';
    }
  }

  public static getDateDiff(date, dateTime = null): void {
    if (date === 0 && dateTime !== null) {
      date = Math.round(dateTime / (60 * 1000)) === 0 ? "Few seconds ago" : Math.round(dateTime / (60 * 1000)) < 60 ? Math.round(dateTime / (60 * 1000)) + " minutes ago" : Math.round(dateTime / (60 * 60 * 1000)) + " hours ago";
    } else if (date < 7) {
      date = date + " days ago";
    } else if (date <= 28) {
      date = Math.round(date / 7) === 1 ? Math.round(date / 7) + " week ago" : Math.round(date / 7) + " weeks ago";
    } else if (date < 365) {
      date = Math.round(date / 30) === 1 ? Math.round(date / 30) + " month ago" : Math.round(date / 30) + " months ago";
    } else {
      date = Math.round(date / 365) === 1 ? +Math.round(date / 365) + " year ago" : Math.round(date / 365) + " years ago";
    }
    return date;
  }

  public static getDaysDiffFromToday(date, beautify = true, condition = 'ceil', alwaysDay: boolean = false, withoutTimezone = false): string | number {
    let date2 = new Date();
    let date1 = new Date(date);
    let tzDifference = 0;
    if (withoutTimezone) {
      tzDifference = date2.getTimezoneOffset() * 60 * 1000;
    }
    let timeDiff = Math.abs(date2.getTime() + tzDifference - date1.getTime());
    let datesDiff;
    if (condition === 'ceil') {
      datesDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    } else {
      datesDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    }
    let daysDifference;
    if (beautify) {
      if (datesDiff < 7 || alwaysDay) {
        if (datesDiff === 1) {
          daysDifference = datesDiff + " Day";
        } else {
          daysDifference = datesDiff + " Days";
        }
      } else if (datesDiff <= 28) {
        daysDifference = Math.round(datesDiff / 7) === 1 ? Math.round(datesDiff / 7) + " Week" : Math.round(datesDiff / 7) + " Weeks";
      } else if (datesDiff < 365) {
        daysDifference = Math.round(datesDiff / 30) === 1 ? Math.round(datesDiff / 30) + " Month" : Math.round(datesDiff / 30) + " Months";
      } else {
        daysDifference = Math.round(datesDiff / 365) === 1 ? +Math.round(datesDiff / 365) + " Year" : Math.round(datesDiff / 365) + " Years";
      }
    } else {
      daysDifference = datesDiff;
    }

    return daysDifference;
  }

  /*merge this 2 functions*/
  public static getTimeDiffFromToday(date) {
    const percentOfferEndDate = new Date(date).getTime();
    const nowDate = new Date().getTime();
    return (nowDate < percentOfferEndDate);
  }

  // Function to format a date as "YYYY-MM-DD"
  public static formatDateYearMonthDay(date) {
    let month = '' + (date.getMonth() + 1),
      day = '' + date.getDate(),
      year = date.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  public static getIntervalFromCronExpression(cronExpression: string) {

    const currentDate = new Date();
    let cronParse: string;
    const options = {
      currentDate: new Date(),
      iterator: true
    };

    try {
      const interval = CronParser.parseExpression(cronExpression, options);
      if (typeof interval.next()['value'] !== 'undefined') {
        const nextDate1 = new Date(interval.next()['value'].toString());
        const nextDate2 = new Date(interval.next()['value'].toString());

        const diff = Math.abs(Math.floor((nextDate2.getTime() - nextDate1.getTime()) / 1000));

        const days = Math.floor(diff / (24 * 60 * 60));
        const leftSec = Math.floor(diff - days * 24 * 60 * 60);

        const hrs = Math.floor(leftSec / (60 * 60));


        if (days > 0) {
          if (days === 1) {
            cronParse = "Every Day";
          } else if (days === 7) {
            cronParse = "Every Week";
          } else if (days === 30 || nextDate1.getMonth() + 1 === nextDate2.getMonth()) {
            cronParse = "Every Month";
          }
        } else if (days === 0) {
          if (hrs === 6) {
            cronParse = "Every 6 hours";
          } else if (hrs === 12) {
            cronParse = "Every 12 hours";
          }
        } else {
          cronParse = "Advanced";
        }
      }
    } catch (e) {

    }

    return cronParse;
  }

  public static getWordpressDate(dateString: string): Date {
    return Utils.getDate(dateString);
  }

  public static intersect(arr1: any[], arr2: any[]): Array<any> {
    return arr1.filter((item: any): boolean => {
      return arr2.indexOf(item) !== -1;
    });
  }

  public static downloadBlob(blob: Blob, name: string): boolean {
    if (navigator['msSaveBlob']) {
      return navigator['msSaveBlob'](blob, name);
    }
    if (blob.type.indexOf("csv") > -1) {
      name = name + ".csv";
    }
    const url: string = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';

    a.setAttribute('href', url);
    a.setAttribute('download', name);
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    // a.remove();
    return null;
  }

  public static wordByCount(word: string, count: number, withCount: boolean = true): string {
    if (+count === 0) {
      return `No ${word}s`;
    } else if (+count === 1) {
      return withCount ? `${count} ${word}` : `${word}`;
    }

    return withCount ? `${count} ${word}s` : `${word}s`;
  }

  public static getWeekName(date) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return days[date.getDay()];
  }

  public static getMonthName(index: number): string {
    return [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ][index];
  }

  public static formatDate(date: Date, format: string): string {
    if (!date || isNaN(date.getDate())) {
      return '';
    }

    if (!format) {
      return date.toString();
    }

    let formatterMap: any = {};
    formatterMap['SS'] = Utils.addZeroes(date.getSeconds(), 2);
    formatterMap['ss'] = Utils.addZeroes(date.getSeconds(), 2);
    formatterMap['mm'] = Utils.addZeroes(date.getMinutes(), 2);
    formatterMap['HH'] = Utils.addZeroes(date.getHours(), 2);
    formatterMap['hh'] = Utils.addZeroes(date.getHours(), 2);
    formatterMap['DD'] = Utils.addZeroes(date.getDate(), 2);
    formatterMap['dd'] = Utils.addZeroes(date.getDate(), 2);
    formatterMap['D'] = date.getDate().toString();
    formatterMap['d'] = date.getDate().toString();
    formatterMap['MMMM'] = Utils.getMonthName(date.getMonth());
    formatterMap['MMM'] = Utils.getMonthName(date.getMonth()).substr(0, 3);
    formatterMap['MM'] = Utils.addZeroes((date.getMonth() + 1), 2);
    formatterMap['M'] = (date.getMonth() + 1).toString();
    formatterMap['YYYY'] = date.getFullYear().toString();
    formatterMap['YYY'] = date.getFullYear().toString();
    formatterMap['YY'] = date.getFullYear().toString();
    formatterMap['Y'] = date.getFullYear().toString();
    formatterMap['yyyy'] = date.getFullYear().toString();
    formatterMap['yyy'] = date.getFullYear().toString();
    formatterMap['yy'] = date.getFullYear().toString();
    formatterMap['y'] = date.getFullYear().toString();
    formatterMap['a'] = (date.getHours() >= 12) ? 'PM' : 'AM';
    let formatters: string[] = Object.keys(formatterMap);

    let formattedDate: string = format.replace(new RegExp(formatters.join('|'), 'g'), (datePart: string): string => formatterMap[datePart]);

    return formattedDate;
  }

  public static formatPaymentDate(dateValue: number) {
        if (!dateValue) return '';

        try {
            let date: Date;
            if (typeof dateValue === 'number' || (typeof dateValue === 'string' && /^\d+$/.test(dateValue))) {
                date = new Date(Number(dateValue) * 1000);
            } else {
                date = new Date(dateValue);
            }
            if (isNaN(date.getTime())) {
                console.warn('Invalid date value:', dateValue);
                return String(dateValue);
            }

            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return String(dateValue);
        }
    }

  public static getDate(date: any): Date {
    if (TypeUtils.isString(date)) {
      date = date
        .split('am').join(' am')
        .split('pm').join(' pm')
        .split('-').join('/')
        .split('T').join(' ')
        .split('.')[0];
    }

    return new Date(date);
  }

  public static arrayToEntities(arr: any[], key: string): any {
    const entities: any = arr.reduce((entities: any, entity: any): any => {
      if (entity[key]) {
        return {
          ...entities,
          [entity[key]]: entity
        };
      }

      return entities;
    }, {});

    return entities;
  }

  public static removeEntityByKey(entities: any, key): any {
    return Object.keys(entities)
      .filter((k: string) => k !== key)
      .reduce((newEntities, k: string) => {
        newEntities[k] = entities[k];
        return newEntities;
      }, {});
  }

  public static uppercaseFirst(str: string): string {
    if (str.length > 0) {
      str = str[0].toUpperCase() + str.substr(1);
    }

    return str;
  }

  public static getExtension(fileName: string): string {
    return fileName.split('.').reverse()[0].split('?')[0].toLowerCase();
  }

  public static b64DecodeUnicode(str) {
    const padding = '='.repeat((4 - str.length % 4) % 4);
    const base64 = (str + padding).replace(/-/g, '+').replace(/_/g, '/');
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return JSON.parse(decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('')));
  }

  public static checkStringInArray(target, pattern) {
    let value = 0;
    pattern.map((word) => {
      value = value + target.includes(word);
    });
    return (value === 1);
  }

  public static checkDevice() {
    const ua = navigator && navigator.userAgent;

    return ua && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua);
  }

  public static removeTrailingSlashes(url) {
    return url.replace(/\/+$/, ''); //Removes one or more trailing slashes from URL
  }

  public static replaceTextInBracers(text, textWithBracers) {
    return text.replace(textWithBracers.old, textWithBracers.new);
  }

  public static detectBrowser(browser) {
    const ua = navigator && navigator.userAgent;

    if (browser === 'safari') {
      return !ua.toLowerCase().includes('chrome') && ua.toLowerCase().includes('safari');
    }
    return ua.toLowerCase().includes(browser);
  }

  public static deepEqual(object1, object2) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    if (keys1.length !== keys2.length) {
      return false;
    }
    for (const key of keys1) {
      const val1 = object1[key];
      const val2 = object2[key];
      const areObjects = TypeUtils.isObject(val1) && TypeUtils.isObject(val2);
      if (
        areObjects && !this.deepEqual(val1, val2) ||
        !areObjects && val1 !== val2
      ) {
        return false;
      }
    }
    return true;
  }

  public static trimHomeUrl(url, homeUrl) {
    return url.replace(homeUrl, '...');
  }

  public static hexToRgbA(hex, opacity) {
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');
      return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')';
    }
    throw new Error('Bad Hex');
  }

  public static colorIsDarkSimple(bgColor) {
    let color = (bgColor.charAt(0) === '#') ? bgColor.substring(1, 7) : bgColor;
    let r = parseInt(color.substring(0, 2), 16); // hexToR
    let g = parseInt(color.substring(2, 4), 16); // hexToG
    let b = parseInt(color.substring(4, 6), 16); // hexToB
    return ((r * 0.299) + (g * 0.587) + (b * 0.114)) <= 186;
  }

  public static toNumber(val) {
    const valArr = val.split("");
    const valFiltered = valArr.filter(x => !isNaN(x) || x === '.');
    return valFiltered.join("");
  }

  public static stripHtmlTags(input): string {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = input;
    return tempDiv.textContent || tempDiv.innerText;
  }

  public static shuffleArray(array) {
    return array.slice().sort(() => Math.random() - 0.5);
  }

  public static removeParticularExperiment(cookie, arrayOfKeysToRemove) {
    try {
      const _cookie = JSON.parse(cookie);
      if (typeof _cookie !== 'object' || _cookie === null) {
        return null;
      }
      for (let cookieKey of arrayOfKeysToRemove) {
        if (cookieKey in _cookie) {
          delete _cookie[cookieKey];
        }
      }
      return _cookie;
    } catch (error) {
      return null;
    }
  }

  public static createParticularCookie(newExperiment, setExistingValues) {
    let valuesToSet: any = newExperiment;
    if (setExistingValues) {
      valuesToSet = {...JSON.parse(setExistingValues), ...valuesToSet};
    }
    const serializedValues = JSON.stringify(valuesToSet);

    return serializedValues;
  }

  public static checkCouponInStructures(couponValue, couponType) {
    const structuresToCheck = [Config.business, Config.ecommerce, Config.agency];

    for (const structure of structuresToCheck) {
      for (const category in structure) {
        if (structure.hasOwnProperty(category)) {
          const categoryValues = structure[category];
          if (categoryValues[couponType] === couponValue) {
            return true; // CouponValue found for the specified payment type
          }
        }
      }
    }
    return false; // CouponValue not found for the specified payment type in any of the structures
  }

  public static isDemoUser(userSubscription, user) {
    return userSubscription && [Config.defaultStarterPlanId, Config.basePlanId].includes(+userSubscription.subscriptionId)
      && ([Config.demoAiPluginId, Config.builder2PluginId].includes(+userSubscription.product_id)
        || !!(user && user.signUpFor && user.signUpFor.LastChosenOption && user.signUpFor.LastChosenOption.answer
          && ['ai-demo-section', 'ai-demo-section-after-signup', 'ai-demo-template', 'ai-demo-template-after-signup'].includes(user.signUpFor.LastChosenOption.answer)));
  }

  public static isHostedTrialUser(userSubscription, user) {
    return userSubscription && ([Config.managerPluginId, Config.aiCreateWebsitePluginId, Config.installBlankPluginId,
            Config.builder2HostedPluginId].includes(+userSubscription.product_id)
        || !!(user && user.signUpFor && user.signUpFor.LastChosenOption && user.signUpFor.LastChosenOption.answer
          && ['ai-hosted-section', 'ai-hosted-section-after-signup', 'install-blank', 'change-hosting', 'ai-convert'].includes(user.signUpFor.LastChosenOption.answer)));
  }

  /**
   * The website generation planner v2 - and the clone / redesign flows built on
   * top of it - is available only for plain WVC subscriptions, i.e. users
   * without API & reseller or agency suite services.
   */
  public static useWebsiteGenerationPlanner(userSubscription: UserSubscription, builderType: 'wvc' | 'section_based' | null): boolean {
    if (builderType !== 'wvc' || !userSubscription) {
      return false;
    }
    return true;
  }

  public static generate10webDateTime(date, timezoneOffset) {
    const year = parseInt(date.substring(0, 4));
    const month = parseInt(date.substring(4, 6)) - 1; // Months are 0-indexed in JavaScript
    const day = parseInt(date.substring(6, 8));
    const hour = parseInt(date.substring(9, 11));
    const minute = parseInt(date.substring(11, 13));
    const second = parseInt(date.substring(13, 15));
    let _date = new Date(year, month, day, hour, minute, second);
    const timezoneOffsetMillis = timezoneOffset * 1000;
    const localTimeInMillis = _date.getTime() + timezoneOffsetMillis;
    const localDate = new Date(localTimeInMillis);

    return localDate;
  }

  public static findPlanForDataCenter(userSubscription: UserSubscription, selectedDataCenter?: string, directPurchasePlanId?: number): UserSubscription {

    if (!!userSubscription?.plans) {
      if (directPurchasePlanId) {
        const _directPurchasePlan = Object.values(userSubscription.plans).find(plan =>
          plan.planId === directPurchasePlanId || plan.couplePlanId === directPurchasePlanId);
        userSubscription.plan = _directPurchasePlan;
      } else {
        userSubscription.plan = userSubscription.plans[selectedDataCenter];
      }
    }
    return userSubscription;
  }
}
